import { Compass, SearchX, Ticket, type LucideIcon } from 'lucide-react';
import InitialsAvatar from '@/components/ui/InitialsAvatar.tsx';
import NoItems from '@/components/ui/NoItems.tsx';
import { Skeleton } from '@/components/ui/Skeleton.tsx';
import { cn } from '@/lib/utils.ts';
import { CANCELLED_RECORD_CLASS } from '@/components/table/_fieldDefinitions/_base/cancelledRecord.ts';
import { BookingStatus } from '@/types/BookingStatus.ts';
import { formatDestination } from '@/features/dashboard/tours/types/DestinationDto.utils.ts';
import type {
  GlobalSearchBookingHitDto,
  GlobalSearchTourHitDto,
} from '@/features/dashboard/globalSearch/types/GlobalSearchResultsDto.ts';

/*
 * Treść wiersza wyniku bez elementu-gospodarza: okno na szerokim ekranie
 * montuje ją w `CommandItem`, ekran telefonu w `<Link>`.
 */

interface PersonLike {
  firstName: string;
  lastName: string;
}

const initialsOf = (person: PersonLike) =>
  `${person.firstName.charAt(0)}${person.lastName.charAt(0)}`.toUpperCase();

const Subtitle = ({ children }: { children: string | null | undefined }) =>
  children ? (
    <span className="block truncate text-xs text-neutral-500">{children}</span>
  ) : null;

export const TourHit = ({ hit }: { hit: GlobalSearchTourHitDto }) => (
  <>
    <span className="flex size-8 shrink-0 items-center justify-center rounded-[9px] bg-neutral-100 text-neutral-600">
      <Compass aria-hidden size={17} />
    </span>
    <span className="min-w-0 flex-1">
      <span className="block truncate font-semibold">{hit.tour.name}</span>
      <Subtitle>{formatDestination(hit.destination)}</Subtitle>
    </span>
  </>
);

/** Uczestnik, zapisujący i oczekujący: osoba plus nazwa wyjazdu, ikona mówi który. */
export const PersonHit = ({
  person,
  tourName,
  icon: Icon,
  cancelled = false,
}: {
  person: PersonLike;
  tourName: string | null | undefined;
  icon: LucideIcon;
  cancelled?: boolean;
}) => (
  <>
    <InitialsAvatar initials={initialsOf(person)} tone="accent" size="sm" />
    <span className="min-w-0 flex-1">
      <span
        className={cn(
          'block truncate font-semibold',
          cancelled && CANCELLED_RECORD_CLASS,
        )}
      >
        {person.firstName} {person.lastName}
      </span>
      <Subtitle>{tourName}</Subtitle>
    </span>
    <Icon aria-hidden size={16} className="shrink-0 text-neutral-400" />
  </>
);

export const BookingHit = ({ hit }: { hit: GlobalSearchBookingHitDto }) => {
  const cancelled = hit.booking.status === BookingStatus.CANCELLED;
  // Starsze API oddaje puste złączenie jako rekord z samymi null, nie jako null.
  const clientName = hit.client?.id
    ? `${hit.client.firstName} ${hit.client.lastName}`
    : null;
  const subtitle = [clientName, hit.tour?.name].filter(Boolean).join(' · ');
  return (
    <>
      <span className="flex size-8 shrink-0 items-center justify-center rounded-[9px] bg-neutral-100 text-neutral-600">
        <Ticket aria-hidden size={17} />
      </span>
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            'block truncate font-semibold',
            cancelled && CANCELLED_RECORD_CLASS,
          )}
        >
          {hit.booking.bookingCode}
        </span>
        <Subtitle>{subtitle}</Subtitle>
      </span>
    </>
  );
};

export const GlobalSearchEmpty = ({ term }: { term: string }) => (
  <NoItems
    icon={<SearchX />}
    title={`Brak wyników dla „${term.trim()}”`}
    description="Spróbuj innej pisowni albo krótszej frazy."
    className="mx-auto items-center py-8 text-center"
  />
);

export const GlobalSearchResultsSkeleton = () => (
  <div className="flex flex-col gap-1 px-4 py-2" aria-busy>
    <Skeleton className="mb-1 h-3 w-16" />
    {Array.from({ length: 3 }, (_, index) => (
      <div key={index} className="flex items-center gap-3 py-2">
        <Skeleton className="size-8 rounded-[9px]" />
        <div className="flex flex-1 flex-col gap-1.5">
          <Skeleton className="h-3.5 w-40" />
          <Skeleton className="h-3 w-24" />
        </div>
      </div>
    ))}
  </div>
);
